import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface Notification {
  id: string;
  type: 'email' | 'chat' | 'candidate' | 'document' | 'task' | 'meeting' | 'alert' | 'system';
  title: string;
  description: string;
  time: string;
  timestamp: Date;
  sender?: string;
  avatar?: string;
  unread: boolean;
  priority: 'high' | 'normal' | 'low';
  actionRequired?: boolean;
  relatedId?: string; // ID of related entity (candidate, task, etc.)
  actions?: {
    label: string;
    action: string;
    style?: 'primary' | 'secondary' | 'danger';
  }[];
}

interface NotificationsState {
  notifications: Notification[];
  filter: 'all' | 'unread' | 'email' | 'chat' | 'alerts' | 'system';
  selectedNotification: string | null;
  maxNotifications: number;

  // Actions
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  updateNotification: (id: string, updates: Partial<Notification>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  setFilter: (filter: 'all' | 'unread' | 'email' | 'chat' | 'alerts' | 'system') => void;
  setSelectedNotification: (id: string | null) => void;
  getFilteredNotifications: () => Notification[];
  getUnreadCount: () => number;
  getNotificationsByType: (type: Notification['type']) => Notification[];
  clearOldNotifications: () => void;

  // System notification triggers
  notifyNewCandidate: (candidateName: string, position: string, matchScore: number) => void;
  notifyTaskDeadline: (taskTitle: string, timeLeft: string) => void;
  notifyInterviewScheduled: (candidateName: string, interviewTime: string) => void;
  notifyDocumentShared: (documentName: string, sharedBy: string) => void;
  notifyEmailReceived: (sender: string, subject: string) => void;
  notifyChatMessage: (sender: string, message: string) => void;
}


export const useNotificationsStore = create<NotificationsState>()(
  devtools(
    (set, get) => ({
      notifications: [], // Start with empty array - real notifications will be added via realtime subscriptions
      filter: 'all',
      selectedNotification: null,
      maxNotifications: 50,

      addNotification: (notificationData) => {
        const newNotification: Notification = {
          ...notificationData,
          id: `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          timestamp: new Date()
        };

        set((state) => {
          const notifications = [newNotification, ...state.notifications];
          // Keep only the most recent notifications
          return {
            notifications: notifications.slice(0, state.maxNotifications)
          };
        }, false, 'notifications/addNotification');
      },

      removeNotification: (id) =>
        set((state) => ({
          notifications: state.notifications.filter(notification => notification.id !== id),
          selectedNotification: state.selectedNotification === id ? null : state.selectedNotification
        }), false, 'notifications/removeNotification'),

      updateNotification: (id, updates) =>
        set((state) => ({
          notifications: state.notifications.map(notification =>
            notification.id === id ? { ...notification, ...updates } : notification
          )
        }), false, 'notifications/updateNotification'),

      markAsRead: (id) =>
        set((state) => ({
          notifications: state.notifications.map(notification =>
            notification.id === id ? { ...notification, unread: false } : notification
          )
        }), false, 'notifications/markAsRead'),

      markAllAsRead: () =>
        set((state) => ({
          notifications: state.notifications.map(notification => ({
            ...notification,
            unread: false
          }))
        }), false, 'notifications/markAllAsRead'),

      setFilter: (filter) =>
        set({ filter }, false, 'notifications/setFilter'),

      setSelectedNotification: (id) =>
        set({ selectedNotification: id }, false, 'notifications/setSelectedNotification'),

      getFilteredNotifications: () => {
        const { notifications, filter } = get();

        switch (filter) {
          case 'unread':
            return notifications.filter(notification => notification.unread);
          case 'email':
            return notifications.filter(notification => notification.type === 'email');
          case 'chat':
            return notifications.filter(notification => notification.type === 'chat');
          case 'alerts':
            return notifications.filter(notification =>
              notification.type === 'alert' || notification.priority === 'high'
            );
          case 'system':
            return notifications.filter(notification => notification.type === 'system');
          default:
            return notifications;
        }
      },

      getUnreadCount: () => {
        const { notifications } = get();
        return notifications.filter(notification => notification.unread).length;
      },

      getNotificationsByType: (type) => {
        const { notifications } = get();
        return notifications.filter(notification => notification.type === type);
      },

      clearOldNotifications: () => {
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        set((state) => ({
          notifications: state.notifications.filter(notification =>
            notification.unread || notification.timestamp > oneDayAgo
          )
        }), false, 'notifications/clearOldNotifications');
      },

      // System notification triggers
      notifyNewCandidate: (candidateName, position, matchScore) => {
        get().addNotification({
          type: 'candidate',
          title: 'Neue Bewerbung eingegangen',
          description: `${candidateName} - ${position} (${matchScore}% Match)`,
          time: 'gerade eben',
          unread: true,
          priority: matchScore >= 90 ? 'high' : 'normal',
          actions: [
            { label: 'Profil anzeigen', action: 'view_profile', style: 'primary' },
            { label: 'Interview planen', action: 'schedule_interview', style: 'secondary' }
          ]
        });
      },

      notifyTaskDeadline: (taskTitle, timeLeft) => {
        get().addNotification({
          type: 'alert',
          title: 'Aufgaben-Deadline',
          description: `"${taskTitle}" - Deadline in ${timeLeft}`,
          time: 'gerade eben',
          unread: true,
          priority: 'high',
          actionRequired: true,
          actions: [
            { label: 'Aufgabe öffnen', action: 'open_task', style: 'primary' },
            { label: 'Verschieben', action: 'reschedule', style: 'secondary' }
          ]
        });
      },

      notifyInterviewScheduled: (candidateName, interviewTime) => {
        get().addNotification({
          type: 'meeting',
          title: 'Interview geplant',
          description: `Interview mit ${candidateName} um ${interviewTime}`,
          time: 'gerade eben',
          unread: true,
          priority: 'normal',
          actions: [
            { label: 'Kalender öffnen', action: 'open_calendar', style: 'primary' },
            { label: 'Details anzeigen', action: 'view_details', style: 'secondary' }
          ]
        });
      },

      notifyDocumentShared: (documentName, sharedBy) => {
        get().addNotification({
          type: 'document',
          title: 'Dokument geteilt',
          description: `"${documentName}" wurde von ${sharedBy} geteilt`,
          time: 'gerade eben',
          sender: sharedBy,
          unread: true,
          priority: 'normal',
          actions: [
            { label: 'Dokument öffnen', action: 'open_document', style: 'primary' }
          ]
        });
      },

      notifyEmailReceived: (sender, subject) => {
        get().addNotification({
          type: 'email',
          title: `Neue E-Mail von ${sender}`,
          description: `Betreff: ${subject}`,
          time: 'gerade eben',
          sender,
          unread: true,
          priority: 'normal',
          actions: [
            { label: 'E-Mail öffnen', action: 'open_email', style: 'primary' },
            { label: 'Antworten', action: 'reply', style: 'secondary' }
          ]
        });
      },

      notifyChatMessage: (sender, message) => {
        get().addNotification({
          type: 'chat',
          title: `Nachricht von ${sender}`,
          description: message.length > 100 ? `${message.substring(0, 100)}...` : message,
          time: 'gerade eben',
          sender,
          unread: true,
          priority: 'normal',
          actions: [
            { label: 'Chat öffnen', action: 'open_chat', style: 'primary' },
            { label: 'Antworten', action: 'reply', style: 'secondary' }
          ]
        });
      }
    }),
    { name: 'notifications-store' }
  )
);